import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { Project } from '../utils/interfaces/types';

interface ProjectsContextValue {
  projects: Project[];
  isLoading: boolean;
  error: string | null;
  loadProjects: () => Promise<void>;
}

const ProjectsContext = createContext<ProjectsContextValue | undefined>(
  undefined,
);

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  const loadProjects = useCallback(async (): Promise<void> => {
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    const promise = (async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('Projects')
          .select('*')
          .order('order', { ascending: true });

        if (fetchError) {
          setError(fetchError.message);
          fetchPromiseRef.current = null; // permite reintentar si falló
          return;
        }

        setProjects((data ?? []) as Project[]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido');
        fetchPromiseRef.current = null; // permite reintentar si falló
      } finally {
        setIsLoading(false);
      }
    })();

    fetchPromiseRef.current = promise;
    return promise;
  }, []);

  return (
    <ProjectsContext.Provider
      value={{ projects, isLoading, error, loadProjects }}
    >
      {children}
    </ProjectsContext.Provider>
  );
}

export function useProjects() {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjects debe usarse dentro de <ProjectsProvider>');
  }
  return context;
}
